import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useStore from '@/components/playbook-builder/hooks/use-store';
import type { Banner } from '../../error-warning-list/error-warning-list';
import type { RulesFormData } from '../rules-node.types';
type FieldPath = `groups.${number}.conditions.${number}.expression`;
type RulesErrorContext = {
  contextType: 'rules_error_context';
  conditionId: string;
  problemMarkers: {
    message: string;
  }[];
};
type ActiveError = {
  conditionId: string;
  fieldPath: FieldPath;
  message: string;
  originalValue: string;
};
const findRulesErrorDetail = (banners: Banner[], nodeId: string) => banners.filter(b => b.nodeId === nodeId).filter(b => b.type === 'error').find(b => (b.description.context as RulesErrorContext)?.contextType === 'rules_error_context');
const findFieldPath = (formGroups: RulesFormData['groups'], conditionId: string): FieldPath | null => {
  for (let g = 0; g < formGroups.length; g++) {
    const c = formGroups[g].conditions.findIndex(cond => cond.id === conditionId);
    if (c !== -1) return `groups.${g}.conditions.${c}.expression`;
  }
  return null;
};
const getErrorMessage = (context: RulesErrorContext, fallbackMessage: string, defaultMessage: string) => context.problemMarkers?.[0]?.message || fallbackMessage || defaultMessage;
type UseRulesErrorHandlerProps = {
  nodeId: string;
  form: UseFormReturn<RulesFormData>;
  formGroups: RulesFormData['groups'];
};
/**
 * Handles backend validation errors for rules conditions.
 * - Shows backend errors on the corresponding form field
 * - Preserves backend errors when form validation clears them on submit
 * - Clears the error when the user edits the field
 */
export const useRulesErrorHandler = t0 => {
  const $ = _c(13);
  const {
    nodeId,
    form,
    formGroups
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "rulesDataNode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    data: t2
  } = useStore();
  const {
    banners
  } = t2;
  const activeErrorRef = useRef(null);
  const dismissedIdRef = useRef(null);
  let t3;
  let t4;
  if ($[1] !== banners || $[2] !== form || $[3] !== formGroups || $[4] !== nodeId || $[5] !== t) {
    t3 = () => {
      const errorDetail = findRulesErrorDetail(banners, nodeId);
      if (!errorDetail) {
        activeErrorRef.current = null;
        dismissedIdRef.current = null;
        return;
      }
      const context = errorDetail.description.context as RulesErrorContext;
      const {
        conditionId
      } = context;
      if (dismissedIdRef.current === conditionId) {
        return;
      }
      const fieldPath = findFieldPath(formGroups, conditionId);
      if (!fieldPath) {
        return;
      }
      const message = getErrorMessage(context, errorDetail.description.message, t(_temp));
      const originalValue = form.getValues(fieldPath);
      activeErrorRef.current = {
        conditionId,
        fieldPath,
        message,
        originalValue
      };
      form.setError(fieldPath, {
        type: "manual",
        message
      });
      form.setFocus(fieldPath);
    };
    t4 = [banners, nodeId, formGroups, form, t];
    $[1] = banners;
    $[2] = form;
    $[3] = formGroups;
    $[4] = nodeId;
    $[5] = t;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== form) {
    t5 = () => {
      const subscription = form.watch((_values, t7) => {
        const {
          name
        } = t7;
        const active = activeErrorRef.current;
        if (!active || name !== active.fieldPath) {
          return;
        }
        if (form.getValues(active.fieldPath) !== active.originalValue) {
          form.clearErrors(active.fieldPath);
          dismissedIdRef.current = active.conditionId;
          activeErrorRef.current = null;
        }
      });
      return () => subscription.unsubscribe();
    };
    t6 = [form];
    $[8] = form;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  if ($[11] !== form) {
    const reapplyBackendError = () => {
      const active_0 = activeErrorRef.current;
      if (active_0) {
        form.setError(active_0.fieldPath, {
          type: "manual",
          message: active_0.message
        });
      }
    };
    t7 = {
      reapplyBackendError
    };
    $[11] = form;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};
function _temp($) {
  return $.conditionInvalid;
}